import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { PageContainer } from '../../../common/components/PageContainer';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { VendorPlan } from '../../../modules/superadmin/our-vendors/models/vendor.model';
import { DealCalculator } from '../../../modules/superadmin/pricing/DealCalculator';
import { money } from '../../../modules/superadmin/pricing/rate-card';

const LADDER = ['trial', 'visualizer', 'growth', 'enterprise'];

/**
 * The pricing system on one page (docs/pricing-v2.md, API repo): the ladder
 * every vendor sits on, and the calculator every Enterprise deal is priced
 * with. A plan opens in the plan editor; a deal is saved on the vendor's page.
 */
export function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plansApi = usePlansApi();
  const [plans, setPlans] = useState<VendorPlan[]>([]);

  useEffect(() => {
    Promise.resolve(plansApi.getPlans())
      .then((rows) => setPlans((rows as VendorPlan[]) || []))
      .catch(() => setPlans([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ladder = LADDER.map((slug) => plans.find((p) => p.slug === slug)).filter(Boolean) as VendorPlan[];
  const legacy = plans.filter((p) => !LADDER.includes(p.slug));

  const priceOf = (p: VendorPlan) => {
    if (p.pricingType === 'trial') return t('PRICING.UPGRADE.TRIAL_DAYS', { count: p.trialDays || 14 });
    if (!p.price) return t('PLANS.PRICE_CUSTOM', 'Custom');
    const base = `${money(p.price, p.currency)}${t('PRICING.UPGRADE.PER_MONTH')}`;
    return p.pricingType === 'custom' ? `${t('PRICING.UPGRADE.FROM')} ${base}` : base;
  };

  return (
    <PageContainer breadcrumbs={[{ label: t('PRICING.ADMIN.TITLE') }]}>
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">{t('PRICING.ADMIN.TITLE')}</h1>
          <p className="mt-1 max-w-[680px] text-sm leading-relaxed text-muted-foreground">{t('PRICING.ADMIN.SUBTITLE')}</p>
        </div>

        <section>
          <p className="mb-3 text-xs font-medium tracking-wider text-muted-foreground uppercase">{t('PRICING.ADMIN.LADDER')}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="pricing-ladder">
            {ladder.map((p, i) => (
              <button
                key={p._id}
                type="button"
                onClick={() => navigate(`/plans/${p._id}/edit`)}
                className="flex flex-col rounded-xl border bg-card p-4 text-start transition-colors hover:border-foreground/40"
                data-testid={`ladder-${p.slug}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground tabular-nums">{i + 1}</span>
                  <span className={cn('rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground')}>
                    {t(`PRICING.ADMIN.HOW.${p.pricingType || 'custom'}`)}
                  </span>
                </div>
                <p className="mt-2 text-base font-medium">{p.name}</p>
                <p className="font-heading mt-1 text-lg tracking-tight">{priceOf(p)}</p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t(`PRICING.ADMIN.FOR.${p.slug}`, { defaultValue: p.tagline || '' })}</p>
              </button>
            ))}
          </div>
          {legacy.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {t('PRICING.ADMIN.LEGACY', { plans: legacy.map((p) => p.name).join(', ') })}
            </p>
          )}
        </section>

        <section>
          <p className="mb-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">{t('PRICING.DEAL.TITLE')}</p>
          <p className="mb-4 max-w-[680px] text-sm text-muted-foreground">{t('PRICING.DEAL.SUBTITLE')}</p>
          <DealCalculator />
        </section>
      </div>
    </PageContainer>
  );
}

export default PricingPage;
